import { createFileRoute, Link } from "@tanstack/react-router"
import { useQuery } from "convex/react"
import { ArrowRight, Briefcase, Building2 } from "lucide-react"
import { api } from "../../convex/_generated/api"
import { EmploymentTypeRail } from "@/components/employment-type-rail"
import Footer from "@/components/footer-1"
import { formatPublishedTally, isPublishedTally } from "@/lib/public-counts"

export const Route = createFileRoute("/jobs/")({
  component: JobsLandingPage,
})

/** Decorative demos only — authored theater, not live listings. */
const DEMO = {
  matched: [
    {
      mark: "W",
      logo: "/demo/whatfix.png",
      title: "Fractional CMO",
      detail: "Whatfix · HSR Layout",
      type: "Fractional",
      chip: "accent" as const,
    },
    {
      mark: "C",
      logo: "/demo/cultfit.png",
      title: "Software Engineer",
      detail: "Cult.fit · HSR Layout",
      type: "Full-time",
      chip: "mute" as const,
    },
  ],
  activity: [
    {
      name: "Profile viewed",
      detail: "2h ago · Whatfix",
      avatar: "/demo/avatar-ananya.jpg",
    },
    {
      name: "Saved internship",
      detail: "Yesterday · Cult.fit",
      avatar: "/demo/avatar-rohan.jpg",
    },
  ],
}

function JobsLandingPage() {
  const counts = useQuery(api.stats.publicCounts)
  const openJobs = counts?.openJobs
  const companies = counts?.companies
  const showOpenJobs = isPublishedTally(openJobs)
  const showCompanies = isPublishedTally(companies)
  const showTallies = showOpenJobs || showCompanies

  return (
    <div className="home-wf home-wf--hero-only home-wf--jobs">
      <div className="home-wf-glow" aria-hidden />

      <main className="home-wf-mist-main">
        <section className="home-wf-hero jobs-hero" aria-labelledby="jobs-hero-heading">
          <div className="home-wf-shell jobs-stage">
            <div className="jobs-split">
              <div className="jobs-copy">
                <h1 id="jobs-hero-heading" className="jobs-title">
                  Find work that{" "}
                  <em className="home-wf-script">matters.</em>
                </h1>
                <p className="jobs-sub">
                  Members-only roles in HSR and neighbourhood — Koramangala,
                  Indiranagar, JP Nagar, Bellandur, and Sarjapur. Browse free,
                  apply when membership unlocks.
                </p>
                <EmploymentTypeRail className="jobs-types" />
                <div className="jobs-ctas">
                  <Link to="/jobs/join" className="home-wf-btn home-wf-btn--lime">
                    Create profile
                    <ArrowRight className="size-4" aria-hidden />
                  </Link>
                  <Link
                    to="/jobs/browse"
                    className="home-wf-btn home-wf-btn--outline"
                  >
                    Browse jobs
                    <ArrowRight className="size-4" aria-hidden />
                  </Link>
                </div>

                {showTallies ? (
                <div
                  className="jobs-tallies"
                  aria-label="Community activity"
                >
                  {showOpenJobs ? (
                  <div className="jobs-tally">
                    <div className="jobs-tally-well" aria-hidden>
                      <Briefcase className="jobs-tally-icon" />
                    </div>
                    <div>
                      <p className="jobs-tally-num tabular-nums">
                        {formatPublishedTally(openJobs)}
                      </p>
                      <p className="jobs-tally-meta">
                        <span className="jobs-tally-live" aria-hidden />
                        <span className="jobs-tally-label">open roles</span>
                      </p>
                    </div>
                  </div>
                  ) : null}
                  {showOpenJobs && showCompanies ? (
                  <div className="jobs-tally-rule" aria-hidden />
                  ) : null}
                  {showCompanies ? (
                  <div className="jobs-tally">
                    <div className="jobs-tally-well" aria-hidden>
                      <Building2 className="jobs-tally-icon" />
                    </div>
                    <div>
                      <p className="jobs-tally-num tabular-nums">
                        {formatPublishedTally(companies)}
                      </p>
                      <p className="jobs-tally-meta">
                        <span className="jobs-tally-live" aria-hidden />
                        <span className="jobs-tally-label">companies</span>
                      </p>
                    </div>
                  </div>
                  ) : null}
                </div>
                ) : null}
              </div>

              <div className="jobs-proof" aria-hidden>
                <div className="jobs-stack">
                  <div className="jobs-card jobs-card--matched">
                    <p className="jobs-card-label">Matched roles · Demo</p>
                    <ul className="jobs-card-list">
                      {DEMO.matched.map((row) => (
                        <li key={row.title} className="jobs-card-row">
                          <img
                            className="jobs-card-logo"
                            src={row.logo}
                            alt=""
                            width={34}
                            height={34}
                            loading="lazy"
                            decoding="async"
                          />
                          <div className="jobs-card-text">
                            <strong>{row.title}</strong>
                            <span>{row.detail}</span>
                          </div>
                          <span className={`home-wf-chip home-wf-chip--${row.chip}`}>
                            {row.type}
                          </span>
                        </li>
                      ))}
                    </ul>
                    <span className="jobs-card-more">
                      View matches
                      <ArrowRight className="size-3.5" aria-hidden />
                    </span>
                  </div>

                  <div className="jobs-card jobs-card--activity">
                    <p className="jobs-card-label">Profile activity · Demo</p>
                    <ul className="jobs-card-list">
                      {DEMO.activity.map((row) => (
                        <li key={row.name} className="jobs-card-row">
                          <img
                            className="jobs-card-avatar"
                            src={row.avatar}
                            alt=""
                            width={34}
                            height={34}
                            loading="lazy"
                            decoding="async"
                          />
                          <div className="jobs-card-text">
                            <strong>{row.name}</strong>
                            <span>{row.detail}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="jobs-card jobs-card--notice">
                    <p className="jobs-card-label jobs-card-label--on-dark">
                      Community notice
                    </p>
                    <p className="jobs-notice-lead">Founder coffee</p>
                    <p className="jobs-notice-meta">
                      Sat 10am · HSR Layout · Members
                    </p>
                    <span className="jobs-notice-chip">Members only</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Footer />
        </section>
      </main>
    </div>
  )
}
